import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, KeyRound } from 'lucide-react';
import { auth } from '@/firebase';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { generateSalt, hashPin } from '@/lib/pinCrypto';

/**
 * Diálogo de recuperación del PIN de administrador.
 *
 * Paso 1: reautenticación con email (fijo, de la sesión) + contraseña de acceso.
 * Paso 2: creación de un PIN nuevo (4-6 dígitos) + confirmación.
 *
 * Seguridad:
 * - Solo disponible para cuentas email/password (providerData con providerId 'password').
 * - Usa reauthenticateWithCredential (reautenticación real de Firebase Auth), nunca
 *   signInWithEmailAndPassword como sustituto.
 * - El PIN anterior permanece intacto hasta que el nuevo se guarda correctamente en Firestore.
 * - La contraseña y los PIN se mantienen solo en estado local y se limpian al cancelar,
 *   cerrar, completar el flujo o desmontar el componente. Nunca se persisten en
 *   localStorage, Firestore ni logs.
 */
export function AdminPinRecoveryDialog({
  open,
  onOpenChange,
  workspaceId,
  updateAdminSecurityConfig,
  onSuccess,
}) {
  const { toast } = useToast();

  const [step, setStep] = useState(1); // 1 = reautenticación, 2 = nuevo PIN
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [reauthError, setReauthError] = useState('');

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pinError, setPinError] = useState('');

  const currentUser = auth.currentUser;
  const email = currentUser?.email || '';
  const providerData = currentUser?.providerData || [];
  const isPasswordProvider = providerData.some((p) => p?.providerId === 'password');
  const isOwner = !!workspaceId && currentUser?.uid === workspaceId;

  const resetState = () => {
    setStep(1);
    setPassword('');
    setReauthError('');
    setNewPin('');
    setConfirmPin('');
    setPinError('');
    setIsVerifying(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  // Limpiar datos sensibles del estado también al desmontar el componente
  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    resetState();
    onOpenChange(false);
  };

  const handleReauthenticate = async () => {
    if (!currentUser || !email) {
      setReauthError('No hay sesión activa. Vuelve a iniciar sesión.');
      return;
    }

    if (!isPasswordProvider) {
      setReauthError('Esta cuenta no usa correo y contraseña. La recuperación no está disponible.');
      return;
    }

    if (!isOwner) {
      setReauthError('Solo el propietario del workspace puede restablecer el PIN de administrador.');
      return;
    }

    if (!password) {
      setReauthError('Introduce tu contraseña.');
      return;
    }

    setIsVerifying(true);
    setReauthError('');

    try {
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Reautenticación correcta: limpiar contraseña del estado inmediatamente
      setPassword('');
      setStep(2);
    } catch (error) {
      console.error('[ADMIN_PIN_RECOVERY] Reauthentication failed:', error?.code || error);
      setReauthError('Contraseña incorrecta. Inténtalo de nuevo.');
      // No se modifica nada más: el PIN antiguo permanece intacto.
    } finally {
      setIsVerifying(false);
    }
  };

  const validatePinFormat = (pin) => /^[0-9]{4,6}$/.test(pin);

  const handleSaveNewPin = async () => {
    setPinError('');

    if (!validatePinFormat(newPin)) {
      setPinError('El PIN debe tener entre 4 y 6 dígitos.');
      return;
    }

    if (newPin !== confirmPin) {
      setPinError('Los PIN no coinciden.');
      return;
    }

    if (!isOwner) {
      setPinError('Solo el propietario del workspace puede restablecer el PIN de administrador.');
      return;
    }

    setIsSaving(true);
    try {
      const salt = await generateSalt();
      const { hash, iterations, version } = await hashPin(newPin, salt);

      const success = await updateAdminSecurityConfig({
        adminPinHash: hash,
        adminPinSalt: salt,
        adminPinIterations: iterations,
        adminPinVersion: version,
      });

      if (!success) {
        // Guardado falló: el PIN antiguo sigue vigente en Firestore, no se toca nada más.
        setPinError('No se pudo guardar el nuevo PIN. Inténtalo de nuevo.');
        setIsSaving(false);
        return;
      }

      // Guardado correcto: limpiar datos sensibles y cerrar
      toast({
        title: '✅ PIN de administrador actualizado',
        description: 'Ya puedes usar tu nuevo PIN.',
        duration: 2500,
      });

      resetState();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('[ADMIN_PIN_RECOVERY] Error saving new PIN:', error);
      setPinError('Error al guardar el nuevo PIN. Inténtalo de nuevo.');
      setIsSaving(false);
    }
  };

  const handleReauthKeyPress = (e) => {
    if (e.key === 'Enter' && !isVerifying) {
      handleReauthenticate();
    }
  };

  const handlePinKeyPress = (e) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSaveNewPin();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleCancel(); else onOpenChange(next); }}>
      <DialogContent
        className="max-w-sm"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-yellow-600" />
            Recuperar PIN de administrador
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            {!isPasswordProvider ? (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Esta cuenta inició sesión con Google. La recuperación de PIN por contraseña
                  no está disponible para este tipo de cuenta.
                </span>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Verifica tu identidad con la contraseña de acceso a Rutapp.
                </p>

                <div>
                  <Label htmlFor="recovery-email" className="text-sm font-medium">
                    Correo
                  </Label>
                  <Input
                    id="recovery-email"
                    type="email"
                    value={email}
                    disabled
                    readOnly
                    className="mt-1 bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="recovery-password" className="text-sm font-medium">
                    Contraseña
                  </Label>
                  <Input
                    id="recovery-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleReauthKeyPress}
                    placeholder="Tu contraseña de acceso"
                    className="mt-1"
                    disabled={isVerifying}
                    autoComplete="current-password"
                  />
                </div>

                {reauthError && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{reauthError}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Identidad verificada. Crea un nuevo PIN de administrador (4 a 6 dígitos).
            </p>

            <div>
              <Label htmlFor="new-admin-pin" className="text-sm font-medium">
                Nuevo PIN
              </Label>
              <Input
                id="new-admin-pin"
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                onKeyPress={handlePinKeyPress}
                placeholder="0000"
                className="mt-1 text-center text-2xl tracking-widest"
                maxLength="6"
                disabled={isSaving}
              />
            </div>

            <div>
              <Label htmlFor="confirm-admin-pin" className="text-sm font-medium">
                Repite el nuevo PIN
              </Label>
              <Input
                id="confirm-admin-pin"
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                onKeyPress={handlePinKeyPress}
                placeholder="0000"
                className="mt-1 text-center text-2xl tracking-widest"
                maxLength="6"
                disabled={isSaving}
              />
            </div>

            {pinError && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{pinError}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isVerifying || isSaving}>
            Cancelar
          </Button>

          {step === 1 && isPasswordProvider && (
            <Button
              onClick={handleReauthenticate}
              disabled={isVerifying || !password}
              className="bg-primary hover:bg-primary/90"
            >
              {isVerifying ? 'Verificando...' : 'Verificar'}
            </Button>
          )}

          {step === 2 && (
            <Button
              onClick={handleSaveNewPin}
              disabled={isSaving || !newPin || !confirmPin}
              className="bg-primary hover:bg-primary/90"
            >
              {isSaving ? 'Guardando...' : 'Guardar nuevo PIN'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
